import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LiveKitRoom } from '@livekit/components-react';
import { fetchToken } from '../video/livekitConfig';
import { useBroadcastGameState } from './useBroadcastGameState';
import BroadcastLayout from './BroadcastLayout';

const stageBg = '#0b3d2e';

const Message = ({ children }) => (
  <div
    style={{
      width: '100vw',
      height: '100vh',
      background: stageBg,
      color: '#f7f4ea',
      display: 'grid',
      placeItems: 'center',
      fontFamily: 'ui-monospace, SFMono-Regular, monospace',
      fontSize: 24,
      letterSpacing: 2,
      padding: 40,
      textAlign: 'center',
    }}
  >
    {children}
  </div>
);

const BroadcastScreen = () => {
  const [searchParams] = useSearchParams();
  const gameId = searchParams.get('gameId') || '';

  const observerIdentity = useMemo(
    () => `obs-${Math.random().toString(36).slice(2, 10)}`,
    []
  );

  const [token, setToken] = useState(null);
  const [serverUrl, setServerUrl] = useState(null);
  const [tokenError, setTokenError] = useState(null);

  // Lock down the page for OBS capture.
  useEffect(() => {
    const prevHtmlBg = document.documentElement.style.background;
    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyMargin = document.body.style.margin;
    document.documentElement.style.background = stageBg;
    document.body.style.overflow = 'hidden';
    document.body.style.margin = '0';
    return () => {
      document.documentElement.style.background = prevHtmlBg;
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.margin = prevBodyMargin;
    };
  }, []);

  useEffect(() => {
    if (!gameId) return undefined;
    let cancelled = false;
    setTokenError(null);
    fetchToken({
      roomName: gameId,
      identity: observerIdentity,
      name: 'Broadcast',
      role: 'observer',
    })
      .then(({ token: t, url }) => {
        if (cancelled) return;
        setToken(t);
        setServerUrl(url);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('broadcast token fetch failed:', e);
        setTokenError(e?.message || 'token fetch failed');
      });
    return () => {
      cancelled = true;
    };
  }, [gameId, observerIdentity]);

  const gameState = useBroadcastGameState(gameId);

  if (!gameId) {
    return (
      <Message>
        ADD <strong>?gameId=&lt;CODE&gt;</strong> TO THE URL TO BROADCAST A GAME.
      </Message>
    );
  }

  if (gameState.status === 'not-found') {
    return <Message>GAME {gameId.toUpperCase()} NOT FOUND.</Message>;
  }

  if (tokenError) {
    return <Message>VIDEO UNAVAILABLE — {tokenError}</Message>;
  }

  if (!token || !serverUrl) {
    return <Message>CONNECTING TO {gameId.toUpperCase()}…</Message>;
  }

  const hostPlayerId =
    gameState.scores.length > 0 ? gameState.players[0] : null;

  return (
    <LiveKitRoom
      serverUrl={serverUrl}
      token={token}
      connect={true}
      video={false}
      audio={false}
      data-lk-theme="default"
    >
      <BroadcastLayout
        gameId={gameId}
        scores={gameState.scores}
        currentRound={gameState.currentRound}
        totalRounds={gameState.totalRounds}
        hostPlayerId={hostPlayerId}
        round={gameState.round}
        results={gameState.results}
        players={gameState.players}
      />
    </LiveKitRoom>
  );
};

export default BroadcastScreen;
